import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { AppointmentTypes } from '../../../types/appointment.types';

/**
 * Status dot + label cell for appointment tables.
 *
 * Live statuses:
 *   checked-in     → success, pulsing
 *   not-checked-in → error
 *   expired        → warning
 *   escalated      → error, pulsing
 *   resolved       → grey
 *
 * Past statuses:
 *   completed       → success
 *   no-checkin      → error
 *   escalated       → warning
 *   cancelled       → grey
 */

type StatusType = AppointmentTypes.LiveStatus | AppointmentTypes.PastStatus;

interface Props {
    status: StatusType;
    variant?: 'live' | 'past';
}

const LIVE_STATUS_CONFIG: Record<AppointmentTypes.LiveStatus, { label: string; colorKey: string; pulse: boolean }> = {
    'checked-in': { label: 'Checked In', colorKey: 'success', pulse: true },
    'not-checked-in': { label: 'Not Checked In', colorKey: 'error', pulse: false },
    'expired': { label: 'Expired', colorKey: 'warning', pulse: false },
    'escalated': { label: 'Escalated', colorKey: 'error', pulse: true },
    'resolved': { label: 'Resolved', colorKey: 'grey', pulse: false },
};

const PAST_STATUS_CONFIG: Record<AppointmentTypes.PastStatus, { label: string; colorKey: string; pulse: boolean }> = {
    'completed': { label: 'Completed', colorKey: 'success', pulse: false },
    'no-checkin': { label: 'No Check-In', colorKey: 'error', pulse: false },
    'escalated': { label: 'Escalated', colorKey: 'warning', pulse: false },
    'cancelled': { label: 'Cancelled', colorKey: 'grey', pulse: false },
};

export const AppointmentStatusCell: React.FC<Props> = ({ status, variant = 'live' }) => {
    const theme = useTheme();

    const config = variant === 'live'
        ? LIVE_STATUS_CONFIG[status as AppointmentTypes.LiveStatus]
        : PAST_STATUS_CONFIG[status as AppointmentTypes.PastStatus];

    if (!config) return null;

    const getColor = (key: string): string => {
        switch (key) {
            case 'success': return theme.palette.success.main;
            case 'error': return theme.palette.error.main;
            case 'warning': return theme.palette.warning.main;
            case 'grey': return theme.palette.grey[500];
            default: return theme.palette.grey[500];
        }
    };

    const dotColor = getColor(config.colorKey);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: dotColor,
                    flexShrink: 0,
                    ...(config.pulse && {
                        animation: 'appointmentStatusPulse 2s ease-in-out infinite',
                        '@keyframes appointmentStatusPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                    }),
                }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {config.label}
            </Typography>
        </Box>
    );
};
